/**
 * Pure mapping helpers for Lot 6.6A prospect import.
 * Auto-map is a suggestion only; the user must review before preview.
 */

export const IMPORT_TARGETS = Object.freeze([
  "address",
  "city",
  "province",
  "postalCode",
  "numberOfUnits",
  "ownerName",
  "ownerMailingAddress",
  "notes",
  // Immo 360 / JLR optional property enrichment (future CSV/paste sources)
  "evaluation",
  "contrepartie",
  "derniereTransaction",
  "nomTransaction",
  "categorieBatiment",
  "typeConstruction",
  "nombreEtages",
  "anneeConstruction",
  "lotNumbers",
  "cadastralDivision",
  "municipalRollNumber",
  "landAreaValue",
  "landAreaUnit",
]);

/** Property-table targets. Owner name/mailing live in the primary-owner section. */
export const PROPERTY_MAPPING_TARGETS = Object.freeze(
  IMPORT_TARGETS.filter((target) => target !== "ownerName" && target !== "ownerMailingAddress"),
);

export const REQUIRED_IMPORT_TARGETS = Object.freeze(["address", "city", "province", "postalCode"]);

export const IGNORE_TARGET = "";

const SYNONYMS = Object.freeze({
  address: Object.freeze([
    "adresse immeuble",
    "adresse",
    "address",
    "street",
    "street address",
    "rue",
  ]),
  city: Object.freeze(["ville immeuble", "ville", "city"]),
  province: Object.freeze(["province", "prov", "state"]),
  postalCode: Object.freeze([
    "code postal immeuble",
    "code postal",
    "postal",
    "postal code",
    "zip",
    "zip code",
  ]),
  numberOfUnits: Object.freeze([
    "nombre logements",
    "nombre de logements",
    "unites",
    "units",
    "nb unites",
    "doors",
  ]),
  ownerName: Object.freeze(["proprietaire", "owner", "owner name", "nom du proprietaire"]),
  ownerMailingAddress: Object.freeze([
    "adresse postale",
    "mailing address",
    "owner address",
    "adresse proprietaire",
  ]),
  notes: Object.freeze(["notes", "note", "commentaires", "comments"]),
  evaluation: Object.freeze(["evaluation", "evaluation municipale", "municipal assessment"]),
  contrepartie: Object.freeze(["contrepartie", "consideration", "sale price"]),
  derniereTransaction: Object.freeze([
    "derniere transaction",
    "date transaction",
    "last transaction",
  ]),
  nomTransaction: Object.freeze(["nom transaction", "type transaction", "transaction type"]),
  categorieBatiment: Object.freeze(["categorie batiment", "building category", "categorie"]),
  typeConstruction: Object.freeze(["type construction", "construction type"]),
  nombreEtages: Object.freeze(["nombre etages", "etages", "floors", "storeys", "stories"]),
  anneeConstruction: Object.freeze(["annee construction", "year built", "construction year"]),
  lotNumbers: Object.freeze(["numero lot", "numeros lot", "lot", "lots", "lot number", "lot numbers"]),
  cadastralDivision: Object.freeze([
    "circonscription",
    "circonscription fonciere",
    "cadastral division",
    "cadastre",
  ]),
  municipalRollNumber: Object.freeze([
    "matricule",
    "numero matricule",
    "roll number",
    "municipal roll",
  ]),
  landAreaValue: Object.freeze([
    "superficie",
    "superficie terrain",
    "land area",
    "area",
    "superficie m2",
  ]),
  landAreaUnit: Object.freeze(["unite superficie", "land area unit", "area unit"]),
});

const FORBIDDEN_NORMALIZED = new Set([
  "status",
  "source",
  "allow duplicate",
  "allowduplicate",
  "last contact",
  "next follow up",
  "created at",
  "updated at",
  "created by",
  "updated by",
  "address key",
  "addresskey",
  "admin targeting keys",
  "admintargetingkeys",
  "admin targeting version",
  "admintargetingversion",
  "admin search address",
  "adminsearchaddress",
]);

export function normalizeHeader(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function targetForHeader(normalized) {
  if (!normalized || FORBIDDEN_NORMALIZED.has(normalized)) return null;
  let best = null;
  let bestLen = -1;
  for (const target of IMPORT_TARGETS) {
    for (const synonym of SYNONYMS[target]) {
      if (normalized === synonym && synonym.length > bestLen) {
        best = target;
        bestLen = synonym.length;
      }
    }
  }
  return best;
}

/**
 * @param {string[]} headers
 * @returns {string[]} target or "" per column
 */
export function suggestMapping(headers) {
  const used = new Set();
  return (headers || []).map((header) => {
    const target = targetForHeader(normalizeHeader(header));
    if (!target || used.has(target)) return IGNORE_TARGET;
    used.add(target);
    return target;
  });
}

/**
 * @param {string[]} mapping
 * @param {number} columnCount
 * @param {{ satisfiedTargets?: string[] }} [options]
 */
export function validateMapping(mapping, columnCount, options = {}) {
  const list = Array.isArray(mapping) ? mapping : [];
  if (list.length !== columnCount) {
    return { ok: false, code: "length", missing: [...REQUIRED_IMPORT_TARGETS] };
  }
  const used = new Set();
  for (const target of list) {
    if (target === IGNORE_TARGET || target == null) continue;
    if (!IMPORT_TARGETS.includes(target)) {
      return { ok: false, code: "unknown", missing: [...REQUIRED_IMPORT_TARGETS] };
    }
    if (used.has(target)) {
      return { ok: false, code: "duplicate", missing: [] };
    }
    used.add(target);
  }
  for (const extra of options.satisfiedTargets || []) {
    if (IMPORT_TARGETS.includes(extra)) used.add(extra);
  }
  const missing = REQUIRED_IMPORT_TARGETS.filter((target) => !used.has(target));
  if (missing.length) {
    return { ok: false, code: "required", missing };
  }
  return { ok: true, code: null, missing: [] };
}

/**
 * @param {string[][]} rows
 * @param {string[]} mapping
 * @returns {object[]}
 */
export function mapRows(rows, mapping) {
  const list = Array.isArray(rows) ? rows : [];
  const map = Array.isArray(mapping) ? mapping : [];
  return list.map((row, index) => {
    const next = { rowNumber: index + 1 };
    map.forEach((target, columnIndex) => {
      if (!target || !IMPORT_TARGETS.includes(target)) return;
      const raw = row?.[columnIndex];
      const value = raw == null ? "" : String(raw).trim();
      if (target === "numberOfUnits" || target === "nombreEtages" || target === "anneeConstruction") {
        if (value === "") return;
        next[target] = value;
        return;
      }
      if (target === "evaluation" || target === "contrepartie" || target === "landAreaValue") {
        if (value === "") return;
        next[target] = value;
        return;
      }
      if (value) next[target] = value;
    });
    return next;
  });
}

/**
 * Canonical allowlist for previewAdminProspectImport request rows.
 * Must stay aligned with IMPORT_ROW_KEYS in prospectImport.js.
 * Transient enrichment mapping targets (evaluation, contrepartie, …) are never
 * allowed at the root — only inside `enrichment`.
 */
export const ADMIN_PROSPECT_IMPORT_PREVIEW_ROW_KEYS = Object.freeze([
  "rowNumber",
  "address",
  "city",
  "province",
  "postalCode",
  "numberOfUnits",
  "ownerName",
  "ownerMailingAddress",
  "owners",
  "notes",
  "warnings",
  "enrichment",
]);

/** Mapping targets normalized into `enrichment` — must not remain on the wire root. */
export const TRANSIENT_IMPORT_MAPPING_FIELD_KEYS = Object.freeze([
  "evaluation",
  "contrepartie",
  "derniereTransaction",
  "nomTransaction",
  "categorieBatiment",
  "typeConstruction",
  "nombreEtages",
  "anneeConstruction",
  "lotNumbers",
  "cadastralDivision",
  "municipalRollNumber",
  "landAreaValue",
  "landAreaUnit",
]);

const PREVIEW_ROW_KEY_SET = new Set(ADMIN_PROSPECT_IMPORT_PREVIEW_ROW_KEYS);

/**
 * Convert an internally mapped row into the wire-safe preview DTO.
 * Call after mapRowsWithProfile so `enrichment` / `owners` already exist.
 * Strips transient enrichment mapping targets from the root.
 *
 * @param {object|null|undefined} row
 * @returns {object|null}
 */
export function toAdminProspectImportPreviewRequestRow(row) {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;
  const next = {};
  for (const key of ADMIN_PROSPECT_IMPORT_PREVIEW_ROW_KEYS) {
    if (Object.prototype.hasOwnProperty.call(row, key)) {
      next[key] = row[key];
    }
  }
  return next;
}

/**
 * @param {object[]} rows
 * @returns {object[]}
 */
export function toAdminProspectImportPreviewRequestRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const out = [];
  for (const row of list) {
    const wire = toAdminProspectImportPreviewRequestRow(row);
    if (wire) out.push(wire);
  }
  return out;
}

/** True when a row still has a forbidden transient enrichment field at the root. */
export function previewRequestRowHasTransientRootFields(row) {
  if (!row || typeof row !== "object") return false;
  for (const key of TRANSIENT_IMPORT_MAPPING_FIELD_KEYS) {
    if (Object.prototype.hasOwnProperty.call(row, key)) return true;
  }
  for (const key of Object.keys(row)) {
    if (!PREVIEW_ROW_KEY_SET.has(key)) return true;
  }
  return false;
}

export function mappingSelectValue(value) {
  return value || IGNORE_TARGET;
}
